import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import * as XLSX from "xlsx";
import SectionHeader from "../components/SectionHeader";

// import manifestForm from "../components/manifestForm";
import { toast } from "react-toastify";
// import manifestPreview from "../components/DockerPreview";
import { getManifests, updateManifest } from "../services/manifestService";
import ManifestForm from "../components/ManifestForm";
import {
  ChevronLeft,
  ChevronRight,
  CircleX,
  EyeIcon,
  FileSpreadsheet,
  PencilIcon,
  PrinterIcon,
} from "lucide-react";
import { getBranches } from "../services/branchService";
import { getTripSheets, updateTripSheet } from "../services/tripSheetService";
import TripSheetForm from "../components/TripSheetForm";
import { useSelector } from "react-redux";
import TripSheetPreview from "../components/TripSheetPreview";

const TripSheetList = () => {
  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const { user } = useSelector((state) => state.user);

  const [selectedBranch, setSelectedBranch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showTripSheetEdit, setShowTripSheetEdit] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [tripsheetList, setTripSheetList] = React.useState([]);
  const [formData, setFormData] = React.useState({});
  const [showTripSheetPreview, setShowTripSheetPreview] = React.useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const printRef = useRef();
  const previewRef = useRef();

  const [branches, setBranches] = useState([]);

  const handleGetTripSheetList = async () => {
    try {
      const params = {
        page,
        limit,
        branchCode: selectedBranch,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        search: search || undefined,
      };
      const response = await getTripSheets(params);
      if (response?.status === 200) {
        setTripSheetList(response?.data?.trips || []);
        setTotalPages(response?.data?.totalPages || 1);
        setPage(response?.data?.currentPage || 1);
        setTotalCount(response?.data?.totalCount || 0);
      } else {
        console.error("Failed to fetch manifest list:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching manifest list:", error);
    }
  };
  // console.log("TripSheet--->", tripsheetList);
  const handleExportData = async () => {
    if (isExporting || totalCount === 0) return;
    setIsExporting(true);
    try {
      // Omit page/limit so the API returns the full filtered dataset, not just the current page
      const params = {
        branchCode: selectedBranch || undefined,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        search: search || undefined,
      };
      const response = await getTripSheets(params);
      const allTripSheets = Array.isArray(response?.data)
        ? response.data
        : response?.data?.trips || [];

      if (allTripSheets.length === 0) {
        toast.error("No data available to export");
        return;
      }

      const exportRows = allTripSheets.map((item) => ({
        "TripSheet No": item?.tripSheetNo ?? "-",
        "Trip Date": formatDate(item?.tripDate),
        "Route Name": item?.routeName ?? "-",
        "Vender Name": item?.vendorName ?? "-",
        "Total Expences": item?.totalExpenses ?? "-",
        "Total Packages": item?.totalPackages ?? "-",
        "Total Weight (Kg)": item?.totalWeight ?? "-",
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "TripSheet List");
      XLSX.writeFile(
        workbook,
        `TripSheet_List_${moment().format("YYYY-MM-DD")}.xlsx`,
      );
    } catch (error) {
      console.error("Error exporting tripsheet list:", error);
      toast.error("Failed to export tripsheet list");
    } finally {
      setIsExporting(false);
    }
  };
  const handleTripSheetSave = async (tripsheet) => {
    try {
      setIsLoading(true);
      const res = await updateTripSheet(tripsheet._id, tripsheet);
      if (res.status === 200) {
        console.log("TripSheet saved successfully:", res);
        handleGetTripSheetList();
        toast.success("TripSheet saved successfully");
        setShowTripSheetEdit(false);
      }
    } catch (error) {
      toast.error(error?.response?.data?.error);

      console.error("Error saving TripSheet:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleGetBranches = async () => {
    try {
      const response = await getBranches();
      if (response?.status === 200) {
        setBranches(response?.data);
      } else {
        console.error("Failed to fetch branches:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };
  useEffect(() => {
    handleGetBranches();
    if (user?.role === "EMPLOYEE") {
      setSelectedBranch(user?.branch?.code || "");
    }
  }, []);
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (selectedBranch) {
        handleGetTripSheetList();
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [selectedBranch, page, limit, fromDate, toDate, search]);

  const handlePrintList = () => {
    const printContents = printRef.current.innerHTML;
    const originalContents = document.body.innerHTML;

    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };
  const handlePrintPreview = () => {
    const printContents = previewRef.current.innerHTML;

    const printWindow = window.open("", "_blank");

    printWindow.document.write(`
    <html>
      <head>
        <title>TripSheet Print</title>

        <!-- Tailwind CDN -->
        <script src="https://cdn.tailwindcss.com"></script>

        <style>
          /* ✅ Proper A4 page setup */
          @page {
            size: A4;
          }

          body {
            margin: 0;
            padding: 0;
            font-family: Arial, sans-serif;
          }
           img {
              image-rendering: crisp-edges;
              max-width: 100%;
              height: auto;
            }
          /* ✅ FIXED container */
          .print-container {
            width: 100%;
            box-sizing: border-box;
             padding: 5px; 
          }

          /* ✅ Table styling */
          table {
            width: 100%;
            border-collapse: collapse;
          }

          thead {
            display: table-header-group; /* repeat header */
          }

          tfoot {
            display: table-footer-group;
          }

          tr {
            page-break-inside: avoid;
          }

          th, td {
            border: 1px solid black;
            padding: 4px;
            font-size: 12px;
          }

          /* ✅ Prevent weird cuts */
          .no-break {
            page-break-inside: avoid;
          }

          /* ✅ Footer fix */
          .footer {
            margin-top: 40px;
          }
        </style>
      </head>

      <body>
        <div class="print-container">
          ${printContents}
        </div>
      </body>
    </html>
  `);

    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return moment(dateStr).format("DD-MM-YYYY");
  };
  const selectedBranchName =
    branches?.find((b) => b._id === selectedBranch)?.branchName ||
    "All Branches";
  return (
    <div className="p-3 bg-slate-200 min-h-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-2">
        <SectionHeader
          title="TripSheet List"
          subTitle="Manage and track all TripSheet"
        />

        <div className="flex items-center gap-3 w-full md:w-auto">
          {/* Date */}
          <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-1">
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500">From</span>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPage(1);
                }}
                className="px-2 py-1 text-sm bg-transparent outline-none"
              />
            </div>
            <span className="text-gray-400">→</span>
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500">To</span>
              <input
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPage(1);
                }}
                className="px-2 py-1 text-sm bg-transparent outline-none"
              />
            </div>
          </div>
          <div>
            <select
              value={selectedBranch}
              onChange={(e) => {
                setSelectedBranch(e.target.value);
                setPage(1);
              }}
              name="branchId"
              className="px-4 py-2 border rounded-lg text-sm bg-white"
            >
              <option value="">Select Branch</option>
              {branches?.map((b) => (
                <option key={b._id} value={b.branchCode}>
                  {b.branchName}
                </option>
              ))}
            </select>
          </div>
          {/* Search */}
          <input
            type="text"
            placeholder="Search pickup, customer, address…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="md:w-72 px-4 py-2 border rounded-lg text-sm "
          />
          <button
            disabled={showTripSheetEdit}
            onClick={handlePrintList}
            className="py-2 bg-white px-6 cursor-pointer rounded-lg hover:bg-sky-50 transition"
          >
            <PrinterIcon
              size={20}
              color={showTripSheetEdit ? "gray" : "black"}
            />
          </button>
          <button
            disabled={showTripSheetEdit || isExporting || totalCount === 0}
            onClick={handleExportData}
            title={
              totalCount === 0 ? "No data available to export" : "Export Data"
            }
            className="py-2 bg-white px-4 cursor-pointer rounded-lg hover:bg-sky-50 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
          >
            <FileSpreadsheet
              size={20}
              color={
                showTripSheetEdit || isExporting || totalCount === 0
                  ? "gray"
                  : "black"
              }
            />
            {/* {isExporting ? "Exporting..." : "Export Data"} */}
          </button>
        </div>
      </div>
      {/* manifest Table - To be implemented */}
      {/* Table */}
      {showTripSheetEdit ? (
        <div>
          <div className="flex justify-between">
            <button
              onClick={() => setShowTripSheetEdit(false)}
              className="px-3 py-2 mb-2 bg-gray-600 text-white rounded text-xs"
            >
              Back to TripSheet List
            </button>
            <div className="flex gap-3">
              <button
                onClick={() => handleTripSheetSave(formData)}
                className="px-3 py-2 mb-2 bg-gray-600 text-white rounded text-xs"
              >
                {isLoading ? "Loading..." : "Save"}
              </button>
              {/* <button
                onClick={() => setShowTripSheetPreview(!showTripSheetPreview)}
                className="px-3 py-2 mb-2 bg-gray-600 text-white rounded text-xs"
              >
                Preview
              </button> */}
            </div>
          </div>

          <TripSheetForm
            tripSheet={formData}
            setTripSheet={setFormData}
            isPreview={true}
          />
        </div>
      ) : (
        <div
          ref={printRef}
          className="bg-white border rounded-xl overflow-hidden"
        >
          <div className="hidden print:block pt-2 py-4">
            <div className="flex justify-between text-sm">
              <div className="">
                <h2 className="text-lg font-bold mb-1">TripSheet List</h2>
                <p>
                  <strong>Branch:</strong> {selectedBranchName}
                </p>
                {/* <p>
                  <strong>Date:</strong> {selectedDate}
                </p> */}
              </div>
              <div className="text-right">
                <p>
                  <strong>Date Range:</strong> {fromDate} to {toDate}
                </p>
              </div>
            </div>
          </div>
          {/* Responsive wrapper: allows horizontal scroll on small screens */}
          <div className="overflow-x-auto">
            <table className="min-w-full border">
              <thead className="bg-gray-100 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3 text-center">TripSheet No</th>
                  <th className="px-4 py-3 border-l">Trip Date</th>
                  <th className="px-4 py-3 border-l">Route Name</th>
                  <th className="px-4 py-3 border-l ">Vender Name</th>
                  <th className="px-4 py-3 border-l text-center">
                    Total Expences
                  </th>
                  <th className="px-4 py-3 border-l text-center ">
                    Total Packages
                  </th>
                  <th className="px-4 py-3 border-l text-center ">
                    Total Weight (Kg)
                  </th>
                  <th className="px-4 py-3 border-l text-center print:hidden">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {tripsheetList?.map((item) => (
                  <tr key={item._id} className="border-t text-sm">
                    <td className="px-4 py-3 border-l font-medium">
                      {item?.tripSheetNo}
                    </td>
                    <td className="px-4 py-3 border-l">
                      {formatDate(item?.tripDate)}
                    </td>

                    <td className="px-4 py-3 border-l text-center ">
                      {item?.routeName}
                    </td>
                    <td className="px-4 py-3 border-l text-center ">
                      {item?.vendorName}
                    </td>
                    <td className="px-4 py-3 border-l text-center ">
                      {item?.totalExpenses}
                    </td>
                    <td className="px-4 py-3 border-l text-center ">
                      {item?.totalPackages}
                    </td>
                    <td className="px-4 py-3 border-l text-center ">
                      {item?.totalWeight}
                    </td>

                    <td className="px-4 py-3 border-l flex items-center justify-center gap-2 text-center print:hidden">
                      <button
                        onClick={() => {
                          setShowTripSheetEdit(true);
                          setFormData(item);
                        }}
                        className="px-3 py-1 bg-gray-600 text-white rounded text-xs"
                      >
                        <PencilIcon size={18} color="white" />
                      </button>
                      <button
                        onClick={() => {
                          setShowTripSheetPreview(true);
                          setFormData(item);
                        }}
                        className="px-3 py-1 bg-gray-600 text-white rounded text-xs"
                      >
                        <EyeIcon size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
                {tripsheetList?.length === 0 && (
                  <tr className="h-80">
                    <td colSpan="8" className="py-6 text-center text-gray-500">
                      No TripSheet for selected date
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination Controls */}
          <div className="flex items-center justify-between p-4 border-t bg-gray-50 print:hidden">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Rows per page:</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="border rounded px-2 py-1 text-sm bg-white"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div className="flex items-center gap-4 text-sm">
              <span className="text-gray-600">
                Page {page} of {totalPages || 1}
              </span>
              <div className="flex gap-1">
                <button
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  disabled={page >= totalPages || totalPages === 0}
                  onClick={() => setPage(page + 1)}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <modal
        isOpen={showTripSheetPreview}
        onClose={() => setShowTripSheetPreview(false)}
      >
        {showTripSheetPreview && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            {/* MODAL BOX */}
            <div className="relative w-full max-w-6xl max-h-[92vh] bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* HEADER */}
              {/* <div className="flex items-center justify-between px-6 py-4 border-b bg-white print:hidden">
                <h2 className="text-lg font-bold">TripSheet Preview</h2>

                <button
                  onClick={() => setShowTripSheetPreview(false)}
                  className="px-4 py-1.5 text-sm font-semibold border rounded-md hover:bg-gray-100 transition"
                >
                  Close
                </button>
              </div> */}
              <div className="flex items-center justify-between px-2 py-2 border-b bg-white print:hidden">
                <h2 className="text-lg font-bold">TripSheet Preview</h2>
                <div className="flex gap-2 items-center">
                  <button
                    onClick={handlePrintPreview}
                    className="px-3 py-2  bg-gray-600 text-white rounded text-xs print:hidden"
                  >
                    <PrinterIcon size={18} color="white" />
                  </button>
                  <button
                    onClick={() => setShowTripSheetPreview(false)}
                    className="px-3 py-2  bg-gray-600 text-white rounded text-xs print:hidden"
                  >
                    <CircleX size={18} color="white" />
                  </button>
                </div>
              </div>
              {/* CONTENT */}
              <div className="overflow-y-auto bg-gray-100 p-6 max-h-[calc(92vh-64px)] print:bg-white print:p-0">
                <div className="bg-white rounded-xl shadow-md p-6 print:shadow-none print:rounded-none">
                  {/* <manifestPreview formData={formData} /> */}
                  <TripSheetPreview
                    tripSheet={formData}
                    printRef={previewRef}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </modal>
    </div>
  );
};

export default TripSheetList;
